// import components
import {
  TextField,
  CircularProgress,
  AutocompleteRenderInputParams
} from '@mui/material';
import { VirtualizedAutocomplete } from 'components/shared';

// import external libraries
import { useTranslation } from 'react-i18next';

// import hooks
import { usePatientSearch } from './PatientSearch.hooks';

// import types
import { Patient } from '../types';
import { StyledPatientSearchBox } from './PatientSearch.styles';

interface PatientSearchProps {
  onSelected: (patient: Patient | null) => void;
}

const getOptionLabel = (option: Patient | string) => {
  if (typeof option === 'string') return option;
  return option.name || '';
};

const PatientSearch = ({ onSelected }: PatientSearchProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'careReceiversHome' });
  const {
    selectedPatient,
    searchQuery,
    setSearchQuery,
    patients,
    isLoading,
    handlePatientChange
  } = usePatientSearch();

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      label={t('searchPatient')}
      variant="standard"
      size="small"
      placeholder={t('searchPatientPlaceholder')}
      slotProps={{
        input: {
          ...params.InputProps,
          endAdornment: (
            <>
              {isLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : null}
              {params.InputProps.endAdornment}
            </>
          )
        }
      }}
    />
  );

  return (
    <StyledPatientSearchBox>
      <VirtualizedAutocomplete
        autoHighlight
        value={selectedPatient}
        onChange={(_, newValue) => handlePatientChange(newValue, onSelected)}
        options={patients}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(
          option: Patient | string,
          value: Patient | string
        ) => {
          if (typeof option === 'string' || typeof value === 'string')
            return false;
          return option.id === value.id;
        }}
        onInputChange={(_, newValue) => setSearchQuery(newValue)}
        renderInput={renderInput}
        noOptionsText={
          searchQuery.length > 0 ? t('noPatients') : t('typeToSearch')
        }
        fullWidth
        size="small"
      />
    </StyledPatientSearchBox>
  );
};

export default PatientSearch;
